import type { MessagingSendMessagePayload } from '@tadpoles/api-client'
import {
    toShortIsoLocalDate,
    type DailyMessages,
    type InstantMessage,
    type MessagingMetadata
} from '@tadpoles/common'

import { compareMessages } from './compare-messages.js'
import type { MessagingConversationProjection, MessagingOutgoingMessage } from './state.js'

export class MessagingConversation {
    readonly subjectKey: string
    readonly view: MessagingConversationProjection
    #dailyMessages = $state<readonly DailyMessages[]>([])
    #messages = $derived(
        this.#dailyMessages.flatMap(({ messages }) => messages).sort(compareMessages)
    )
    #metadata = $state<MessagingMetadata>()
    #outgoingMessages = $state<readonly MessagingOutgoingMessage[]>([])

    public constructor(subjectKey: string) {
        this.subjectKey = subjectKey
        const readMessages = () => this.#messages
        const readMetadata = () => this.#metadata
        const readOutgoingMessages = () => this.#outgoingMessages
        this.view = {
            subjectKey,
            get messages() {
                return readMessages()
            },
            get metadata() {
                return readMetadata()
            },
            get outgoingMessages() {
                return readOutgoingMessages()
            }
        }
    }

    public get metadata(): MessagingMetadata | undefined {
        return this.#metadata
    }

    public get oldestDate(): string | undefined {
        return this.#dailyMessages[0]?.date
    }

    public setMetadata(metadata: MessagingMetadata | undefined): void {
        this.#metadata = metadata
    }

    public knownRevision(timestamp: number): number | undefined {
        const date = toShortIsoLocalDate(new Date(timestamp))
        return this.#dailyMessages.find((daily) => daily.date === date)?.revision
    }

    public snapshot() {
        return { dailyMessages: this.#dailyMessages, metadata: this.#metadata }
    }

    public restore(dailyMessages: readonly DailyMessages[], metadata?: MessagingMetadata): void {
        this.#dailyMessages = [...dailyMessages].sort((left, right) =>
            left.date.localeCompare(right.date)
        )
        this.#metadata = metadata
        this.#outgoingMessages = []
    }

    public mergeHistory(incoming: readonly DailyMessages[]): void {
        this.#dailyMessages = this.#mergeDailyMessages(
            this.#dailyMessages,
            incoming.filter((daily) => daily.subjectKey === this.subjectKey)
        )
        const messageIds = new Set(this.#messages.map(({ id }) => id))
        this.#outgoingMessages = this.#outgoingMessages.filter(
            ({ message }) => !messageIds.has(message.id)
        )
    }

    public confirmSend(payload: MessagingSendMessagePayload): void {
        this.#dailyMessages = this.#mergeDailyMessages(this.#dailyMessages, [
            {
                subjectKey: payload.subjectKey,
                date: payload.date,
                revision: payload.revision,
                messages: [payload.message]
            }
        ])
        if (this.#messages.some(({ id }) => id === payload.message.id)) {
            this.#outgoingMessages = this.#outgoingMessages.filter(
                ({ message }) => message.id !== payload.message.id
            )
        }
    }

    public addOutgoing(message: InstantMessage): void {
        this.#outgoingMessages = [...this.#outgoingMessages, { message, status: 'pending' }]
    }

    public retrySend(messageId: string): InstantMessage {
        const outgoing = this.#outgoingMessages.find(({ message }) => message.id === messageId)
        if (!outgoing || outgoing.status !== 'failed') {
            throw new Error('Only a definitively failed message can be retried.')
        }
        this.#replaceOutgoing({ message: outgoing.message, status: 'pending' })
        return outgoing.message
    }

    public markFailed(messageId: string, errorMessage: string): void {
        const outgoing = this.#outgoingMessages.find(({ message }) => message.id === messageId)
        if (outgoing) {
            this.#replaceOutgoing({ message: outgoing.message, status: 'failed', errorMessage })
        }
    }

    public clear(): void {
        this.#dailyMessages = []
        this.#metadata = undefined
        this.#outgoingMessages = []
    }

    #replaceOutgoing(replacement: MessagingOutgoingMessage): void {
        this.#outgoingMessages = this.#outgoingMessages.map((outgoing) =>
            outgoing.message.id === replacement.message.id ? replacement : outgoing
        )
    }

    #mergeDailyMessages(
        existing: readonly DailyMessages[],
        incoming: readonly DailyMessages[]
    ): DailyMessages[] {
        const byDate = new Map(existing.map((daily) => [daily.date, daily]))

        for (const next of incoming) {
            const current = byDate.get(next.date)
            if (!current) {
                byDate.set(next.date, this.#sortDailyMessages(next))
                continue
            }

            if (next.revision < current.revision) {
                continue
            }

            byDate.set(next.date, {
                ...next,
                revision: Math.max(current.revision, next.revision),
                messages: this.#mergeMessages(current.messages, next.messages)
            })
        }

        return [...byDate.values()].sort((left, right) => left.date.localeCompare(right.date))
    }

    #mergeMessages(
        existing: readonly InstantMessage[],
        incoming: readonly InstantMessage[]
    ): InstantMessage[] {
        const byId = new Map(existing.map((message) => [message.id, message]))
        for (const next of incoming) {
            const current = byId.get(next.id)
            if (!current || this.#isNewerMessage(next, current)) {
                byId.set(next.id, next)
            }
        }

        return [...byId.values()].sort(compareMessages)
    }

    #sortDailyMessages(value: DailyMessages): DailyMessages {
        return {
            ...value,
            messages: [...value.messages].sort(compareMessages)
        }
    }

    #isNewerMessage(candidate: InstantMessage, current: InstantMessage): boolean {
        const candidateRevision = candidate.v ?? 0
        const currentRevision = current.v ?? 0
        return (
            candidateRevision > currentRevision ||
            (candidateRevision === currentRevision &&
                candidate.clientUpdateTimestamp >= current.clientUpdateTimestamp)
        )
    }
}
